import { Data, FileUtils, Model, NotifyUtils, Utils } from '@singularsystems/neo-core';
import { AxiosPromise, AxiosResponse } from 'axios';
import { injectable } from 'inversify';
import { NotificationDuration } from '../../App/Models/Enums/NotificationDuration';
import { AppService, Types } from '../../App/Services/AppService';
import SecurityHoldingSearchCriteria from '../../Reports/Holdings/Models/Queries/SecurityHoldingSearchCriteria';
import TransactionSearchCriteria from '../../Reports/Transactions/Models/Queries/TransactionSearchCriteria';
import { TextConstants } from '../TextConstants';
import moment from 'moment';
import CommandResult from '../Models/CommandResult';
import ProvisionalTaxReportsSearchCriteria from '../../Reports/ProvisionalTaxReports/Models/Queries/ProvisionalTaxReportsSearchCriteria';


export interface IFileDownloadApiClient {
    downloadProvisionalTaxReportsExcel(query: Model.PartialPlainObject<ProvisionalTaxReportsSearchCriteria>): any;
    downloadHoldingsReportFile(query: Model.PartialPlainObject<SecurityHoldingSearchCriteria>): any;
    downloadTransactionsReportFile(query: Model.PartialPlainObject<TransactionSearchCriteria>): any;
    saveDocument(document: Model.PartialPlainObject<Document>): AxiosPromise<Model.PlainTrackedObject<CommandResult>>;
    uploadSecurityCSV(fileList: File[] | FileList, serviceProviderID: number, csvSecurityNotes: string, serviceProviderGroupingName: string): Promise<AxiosResponse<string>>
    uploadCashCSV(fileList: File[] | FileList, serviceProviderID: number, csvCashNotes: string, serviceProviderGroupingName: string): Promise<AxiosResponse<string>>
}

@injectable()
export default class FileDownloadApiClient extends Data.ApiClientBase implements IFileDownloadApiClient {
    constructor(config = AppService.get(Types.Config)) {
        super(`${config.apiPath}/FileDownload`)
    }
    downloadProvisionalTaxReportsExcel(query: Model.PartialPlainObject<ProvisionalTaxReportsSearchCriteria>) {
        return this.axios.get(`${this.apiPath}/downloadProvisionalTaxReportsExcel?${Utils.getQueryString(query)}`, {responseType: "blob"})
            .then(function (response: AxiosResponse<any>) {
                FileUtils.showSaveFile(response);
            });
    }

    public downloadHoldingsReportFile(query: Model.PartialPlainObject<SecurityHoldingSearchCriteria>) {
        this.axios.get(`${this.apiPath}/downloadHoldingsReportFile?${Utils.getQueryString(query)}`)
            .then(function (response: AxiosResponse<any>) {
                finaliseFileDownload(response)
            });
    }

    public downloadTransactionsReportFile(query: Model.PartialPlainObject<TransactionSearchCriteria>) {
        this.axios.get(`${this.apiPath}/downloadTransactionsReportFile?${Utils.getQueryString(query)}`)
            .then(function (response) {
                finaliseFileDownload(response)
            });
    }

    public downloadTransactionsFile(query: Model.PartialPlainObject<TransactionSearchCriteria>, providerName: string) {
        this.axios.get(`${this.apiPath}/downloadTransactionsFile?${Utils.getQueryString(query)}`)
            .then(function (response) {
                finaliseSecurityCsvFileDownload(response, providerName)
            });
    }

    public downloadCashTransactionsFile(query: Model.PartialPlainObject<TransactionSearchCriteria>, providerName: string) {
        this.axios.get(`${this.apiPath}/downloadCashTransactionsFile?${Utils.getQueryString(query)}`)
            .then(function (response) {
                finaliseCashCsvFileDownload(response, providerName)
            });
    }

    public uploadSecurityCSV(fileList: File[] | FileList, serviceProviderID: number, csvSecurityNotes: string, serviceProviderGroupingName: string): Promise<AxiosResponse<string>> {
        const bodyFormData = new FormData();
        bodyFormData.set('files', fileList[0]);
        bodyFormData.set('serviceProviderID', serviceProviderID.toString());
        bodyFormData.set('serviceProviderGroupingName', serviceProviderGroupingName);
        bodyFormData.set('csvSecurityNotes', csvSecurityNotes);
    
        return this.axios.post<string>(`${this.apiPath}/uploadSecurityCSV`, bodyFormData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            responseType: 'text' 
        })
        .then((response) => {
            const responseText = response.data;
            if (responseText.includes('Success')) {
                return response;
            } else {
                const blob = new Blob([response.data], { type: 'text/plain' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = serviceProviderGroupingName + "_" + getCurrentDateInCustomFormat()+"_SecurityTransactions_Errors.txt"; 
                document.body.appendChild(link);
                link.click();
                setTimeout(() => document.body.removeChild(link), 0);
                return response;
            }
        })
        .catch((error) => {
            throw error; 
        });
    }    

    public uploadCashCSV(fileList: File[] | FileList, serviceProviderID: number, csvCashNotes: string, serviceProviderGroupingName: string): Promise<AxiosResponse<string>> {
        const bodyFormData = new FormData();
        bodyFormData.set('files', fileList[0]);
        bodyFormData.set('serviceProviderID', serviceProviderID.toString());
        bodyFormData.set('serviceProviderGroupingName', serviceProviderGroupingName.toString());
        bodyFormData.set('csvCashNotes', csvCashNotes);
    
        return this.axios.post<string>(`${this.apiPath}/uploadCashCSV`, bodyFormData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            responseType: 'text' 
        })
        .then((response) => {
            const responseText = response.data;
            if (responseText.includes('Success')) {
                return response;
            } else {
                const blob = new Blob([response.data], { type: 'text/plain' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = serviceProviderGroupingName + "_" + getCurrentDateInCustomFormat()+"_CashTransactions_Errors.txt"; 
                document.body.appendChild(link);
                link.click();
                setTimeout(() => document.body.removeChild(link), 0);
                return response;
            }
        })
        .catch((error) => {
            throw error; 
        });
    }    

        public saveDocument(document: Model.PartialPlainObject<Document>): AxiosPromise<Model.PlainTrackedObject<CommandResult>> {
        return this.axios.post(`${this.apiPath}/documents/save`, document);
    }    
}

    function finaliseFileDownload(response: AxiosResponse<any>){
    if (response.data.ErrorMessage !== undefined ) {
        NotifyUtils.addDanger(TextConstants.Titles.FailedFileDownload, response.data.ErrorMessage, NotificationDuration.Long);
    }
    else {
         var blob = new Blob([response.data], {});
         var a = document.createElement("a");
         var url = window.URL.createObjectURL(blob);
         a.href = url;
         const regExpFilename = /filename*?=['"]?(?:UTF-\d['"]*)?([^;\r\n"']*)['"]?;?/g;
         const filename: string | null = regExpFilename.exec(response.headers["content-disposition"])?.[1] ?? getCurrentDateInCustomFormat()+".csv";
         a.download = filename;
         a.click();
         window.URL.revokeObjectURL(url);
        

        NotifyUtils.addSuccess(TextConstants.Titles.Success, TextConstants.Notifications.FileDownloadSuccessful, NotificationDuration.Standard);
    }
}

function finaliseSecurityCsvFileDownload(response: AxiosResponse<any>, providerName: string){
    if (response.data.ErrorMessage !== undefined ) {
        NotifyUtils.addDanger(TextConstants.Titles.FailedFileDownload, response.data.ErrorMessage, NotificationDuration.Long);
    }
    else {
        var blob = new Blob([response.data], {});
        var a = document.createElement("a");
        var url = window.URL.createObjectURL(blob);
        a.href = url;
        const regExpFilename = /filename*?=['"]?(?:UTF-\d['"]*)?([^;\r\n"']*)['"]?;?/g;
        const filename: string | null = (providerName + "_Security" + (regExpFilename.exec(response.headers["content-disposition"])?.[1] ?? ("_" + getCurrentDateInCustomFormat()+".csv")));
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);

        NotifyUtils.addSuccess(TextConstants.Titles.Success, TextConstants.Notifications.FileDownloadSuccessful, NotificationDuration.Standard);
    }
}

function finaliseCashCsvFileDownload(response: AxiosResponse<any>, providerName: string){
    if (response.data.ErrorMessage !== undefined ) {
        NotifyUtils.addDanger(TextConstants.Titles.FailedFileDownload, response.data.ErrorMessage, NotificationDuration.Long);
    }
    else {
        var blob = new Blob([response.data], {});
        var a = document.createElement("a");
        var url = window.URL.createObjectURL(blob);
        a.href = url;
        const regExpFilename = /filename*?=['"]?(?:UTF-\d['"]*)?([^;\r\n"']*)['"]?;?/g;
        const filename: string | null = providerName + "_Cash" + (regExpFilename.exec(response.headers["content-disposition"])?.[1] ?? ("_" + getCurrentDateInCustomFormat() + ".csv"));
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);

        NotifyUtils.addSuccess(TextConstants.Titles.Success, TextConstants.Notifications.FileDownloadSuccessful, NotificationDuration.Standard);
    }
}

function errorFileDownload(response: AxiosResponse<any>){
    if (response.data.ErrorMessage !== undefined ) {
        NotifyUtils.addDanger(TextConstants.Titles.FailedFileDownload, response.data.ErrorMessage, NotificationDuration.Long);
    }
    else {
        var blob = new Blob([response.data], {});
        var a = document.createElement("a");
        var url = window.URL.createObjectURL(blob);
        a.href = url;
        const regExpFilename = /filename*?=['"]?(?:UTF-\d['"]*)?([^;\r\n"']*)['"]?;?/g;
        const filename: string | null = regExpFilename.exec(response.headers["content-disposition"])?.[1] ?? getCurrentDateInCustomFormat()+".csv";
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);

        NotifyUtils.addSuccess(TextConstants.Titles.Success, TextConstants.Notifications.FileDownloadSuccessful, NotificationDuration.Standard);
    }
}

function getCurrentDateInCustomFormat () {
    var currentDate: string = moment.utc(new Date().toISOString()).format("yyyyMMDDHHmmss");
    return currentDate;
}

